import React, { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { HexColorPicker } from "react-colorful";

// Compact inline colour control: [swatch][#hex] with a popover picker.
// Update 15: the popover renders via a portal to document.body so it escapes any
// `overflow: hidden` / stacking context on the editor panel (z-index fix).
export default function ColourControl({ value, onChange, testid }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const wrapRef = useRef(null);
  const popRef = useRef(null);
  const v = value || "#000000";

  const place = () => {
    const r = wrapRef.current?.getBoundingClientRect();
    if (r) setPos({ top: r.bottom + 6, left: Math.max(8, Math.min(r.left, window.innerWidth - 220)) });
  };
  const toggle = () => { place(); setOpen((o) => !o); };

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (wrapRef.current?.contains(e.target) || popRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    const onScroll = () => setOpen(false);
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    window.addEventListener("scroll", onScroll, true);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("scroll", onScroll, true);
    };
  }, [open]);

  const setHex = (h) => {
    let s = (h || "").trim();
    if (s && !s.startsWith("#")) s = "#" + s;
    onChange(s);
  };

  return (
    <div className="relative inline-flex items-center gap-2" ref={wrapRef}>
      <button type="button" onClick={toggle} aria-label="Pick colour"
              data-testid={testid ? `${testid}-swatch` : undefined}
              className="w-7 h-7 rounded-[4px] border border-[#2a2a2a] shrink-0 cursor-pointer" style={{ background: v }} />
      <input className="st-input st-mono text-[11px] px-2 py-1 w-[92px]" value={v}
             onChange={(e) => setHex(e.target.value)} maxLength={7} data-testid={testid} />
      {open && createPortal(
        <div ref={popRef} style={{ position: "fixed", top: pos.top, left: pos.left, zIndex: 9999 }}
             className="p-2 bg-[#0e0e0e] border border-[#222] rounded-[8px] shadow-xl colour-picker-popover"
             data-testid={testid ? `${testid}-popover` : undefined}>
          <HexColorPicker color={v} onChange={onChange} />
        </div>,
        document.body
      )}
    </div>
  );
}
